import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { format } from "date-fns";
import { getPdfConfig } from "./table.configs/pdf";

/**
 * ReportDataTable - Displays individual report data rows
 * Similar to InvoiceTable/ManifestTable but simpler for report data
 */
const ReportDataTable = ({ data, reportConfig }) => {
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0";
    return value.toLocaleString();
  };

  const formatCellValue = (value, fieldName) => {
    if (value === null || value === undefined) return "-";

    // Handle objects (like populated driver/employee)
    if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      if (value.firstName && value.lastName) {
        return `${value.firstName} ${value.lastName}`;
      }
      if (value.fullName) return value.fullName;
      if (value.name) return value.name;
      if (value.email) return value.email;
      return "[Object]";
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.length > 0
        ? `${value.length} item${value.length !== 1 ? "s" : ""}`
        : "-";
    }

    // Handle dates
    if (
      value instanceof Date ||
      (typeof value === "string" && !isNaN(Date.parse(value)))
    ) {
      try {
        return format(new Date(value), "MMM dd, yyyy");
      } catch {
        return String(value);
      }
    }

    // Handle numbers
    if (typeof value === "number") {
      const lowerField = fieldName.toLowerCase();
      const isCurrency =
        (/total|amount|pay|charge|price|cost/i.test(lowerField) &&
          !/mileage|hours|count|quantity/i.test(lowerField)) ||
        /mileagepay|mileagetotal|payoutmileage/i.test(lowerField) ||
        lowerField === "total" ||
        lowerField === "amount";

      if (isCurrency) {
        return formatCurrency(value);
      }
      return formatNumber(value);
    }

    return String(value);
  };

  const formatColumnHeader = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\b\w/g, (str) => str.toUpperCase())
      .replace(/\s+/g, " ")
      .trim();
  };

  // Get column config from table.configs
  const modelName = reportConfig?.modelName || "";
  const { pdfConfig } = getPdfConfig(modelName);
  const columnDefinitions = pdfConfig?.COLUMN_DEFINITIONS || [];

  // If we have column definitions from config, use them
  // Otherwise, fall back to auto-generated columns
  const columnDefs = React.useMemo(() => {
    if (columnDefinitions.length > 0) {
      // Use config-based columns
      return columnDefinitions.map((col) => ({
        accessorKey: col.accessorKey || col.id,
        header: col.header || formatColumnHeader(col.id || col.accessorKey),
        cell:
          col.cell ||
          (({ getValue }) =>
            formatCellValue(getValue(), col.id || col.accessorKey)),
      }));
    } else {
      // Fallback to auto-generated columns (existing logic)
      const firstRow = data[0] || {};
      const columns = Object.keys(firstRow).filter(
        (key) => !["__v", "_id"].includes(key) && !key.startsWith("_")
      );
      const maxColumns = reportConfig?.maxColumns || 8;
      const selectedColumns = columns.slice(0, maxColumns);

      return selectedColumns.map((col) => ({
        accessorKey: col,
        header: formatColumnHeader(col),
        cell: ({ getValue }) => formatCellValue(getValue(), col),
      }));
    }
  }, [columnDefinitions, data, reportConfig]);

  const table = useReactTable({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  const tableClasses = classNames("border-collapse border border-gray-300");
  const theadClasses = classNames("bg-gray-100 font-times-new-roman");
  const trClasses = "";
  const thClasses = classNames(
    "bg-gray-100 py-2 text-left px-1 border border-gray-300"
  );
  const tdClasses = classNames("py-2 px-1 border border-gray-300");

  return (
    <table id="report-data-table" className={tableClasses}>
      <thead className={theadClasses}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr className={trClasses} key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className={thClasses}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className={tdClasses}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ReportDataTable;
